import { MeasuredSkeletonContainer } from "./MeasuredSkeletonContainer";
import { Box } from "../../../../../mads";
import { Skeleton, SkeletonVariant } from "../../../../../mads/Skeleton";
import { useViewjobProctorAllocation } from "../../../../proctor/__generated__/ViewjobProctorContext";
import { MobVjJobSummaryTst } from "../../../../proctor/__generated__/ViewjobProctorSpecifications";

import { StyleSheet } from "react-native";

interface SkeletonProps {
  width: number;
  height: number;
}

const SkeletonBar = ({ width, height }: SkeletonProps) => {
  return (
    <Box sx={styles.skeletonBar}>
      <Skeleton variant={SkeletonVariant.SQUARE} accessible={false} sx={{ width, height }} />
    </Box>
  );
};

const getSkeletonWidth = (containerWidth: number, offset: number) =>
  Math.max(containerWidth - offset, 0);

const styles = StyleSheet.create({
  root: {
    display: "flex",
    flexBasis: "auto",
    flexGrow: 1,
    flexShrink: 1,
    padding: 20,
    marginTop: 30,
  },
  break: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skeletonBar: {
    margin: 3,
  },
  mt10: {
    marginTop: 10,
  },
  mt40: {
    marginTop: 40,
  },
  mb20: {
    marginBottom: 20,
  },
  mb40: {
    marginBottom: 40,
  },
  pl25: {
    paddingLeft: 25,
  },
});

export function LoadingIndicator(): React.JSX.Element {
  const mobVjJobSummaryTstActive = useViewjobProctorAllocation(MobVjJobSummaryTst).value > 0;
  const dividerHeight = mobVjJobSummaryTstActive ? 8 : 1;

  return (
    <MeasuredSkeletonContainer testID="loading-indicator" sx={styles.root}>
      {(width) => {
        const widthFromOffset = (offset: number) => getSkeletonWidth(width, offset);

        return (
          <>
            <Box sx={styles.break}>
              <SkeletonBar height={40} width={widthFromOffset(120)} />
            </Box>

            <Box sx={styles.break}>
              <SkeletonBar height={20} width={widthFromOffset(200)} />
              <SkeletonBar height={20} width={20} />
            </Box>

            <Box sx={[styles.break, styles.mt40]}>
              <SkeletonBar height={20} width={20} />
              <SkeletonBar height={20} width={widthFromOffset(220)} />
            </Box>

            <SkeletonBar height={10} width={140} />

            <Box sx={[styles.break, styles.mt40, styles.mb20]}>
              <SkeletonBar height={dividerHeight} width={widthFromOffset(100)} />
            </Box>

            <SkeletonBar height={20} width={widthFromOffset(200)} />

            <Box sx={[styles.break, styles.mt10]}>
              <SkeletonBar height={20} width={20} />
              <SkeletonBar height={20} width={widthFromOffset(220)} />
            </Box>
            <Box sx={styles.pl25}>
              <SkeletonBar height={18} width={widthFromOffset(300)} />
            </Box>

            <Box sx={[styles.break, styles.mt10]}>
              <SkeletonBar height={20} width={20} />
              <SkeletonBar height={20} width={widthFromOffset(180)} />
            </Box>
            <Box sx={styles.pl25}>
              <SkeletonBar height={18} width={widthFromOffset(220)} />
            </Box>

            <Box sx={[styles.break, styles.mt10]}>
              <SkeletonBar height={20} width={20} />
              <SkeletonBar height={20} width={widthFromOffset(180)} />
            </Box>
            <Box sx={styles.pl25}>
              <SkeletonBar height={18} width={widthFromOffset(320)} />
            </Box>

            <Box sx={[styles.break, styles.mt40, styles.mb40]}>
              <SkeletonBar height={dividerHeight} width={widthFromOffset(100)} />
            </Box>

            <Box sx={styles.break}>
              <SkeletonBar height={20} width={widthFromOffset(280)} />
            </Box>

            <Box sx={[styles.break, styles.mt10]}>
              <SkeletonBar height={200} width={widthFromOffset(100)} />
            </Box>
          </>
        );
      }}
    </MeasuredSkeletonContainer>
  );
}

export function JobCardLoadingIndicator(): React.JSX.Element {
  return (
    <MeasuredSkeletonContainer testID="job-card-loading-indicator" sx={styles.root}>
      {(width) => (
        <>
          <Box sx={styles.break}>
            <SkeletonBar height={48} width={getSkeletonWidth(width, 100)} />
          </Box>

          <Box sx={[styles.break, styles.mt10]}>
            <SkeletonBar height={180} width={getSkeletonWidth(width, 100)} />
          </Box>
        </>
      )}
    </MeasuredSkeletonContainer>
  );
}
